import axios, { AxiosRequestConfig } from "axios";
import sha256 from 'sha256';
import { AccessKey, ObjectToAccessKey } from "../Objects/AccessKey";
import { AccessLevel, ObjectToAccessLevel } from "../Objects/AccessLevel";
import { ObjectToStaffData, StaffData } from "../Objects/StaffData";
import { ObjectToStoreData, ObjectToStoreDataProps, StoreData } from "../Objects/StoreInterface";
import { ApiError } from "./ApiError";

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = 'X-CSRF-Token';

export interface StaffLoginData {
    stf_id: number,
    stf_name: string,
    stf_access_list: string[]
}

/**
 * getSeed
 * @returns string
 */
export const getSeed = async (config?: AxiosRequestConfig): Promise<string> => {
    try {
        // let params = new URLSearchParams()

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/get_seed', undefined, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return res.data.seed
    } catch (error) {
        throw error
    }
}

/**
 * login
 * @param seed 
 * @param login_str 
 * @param login_pass 
 * @param access_key 
 * @returns 
 */
export const login = async (
    seed: string,
    login_str: string,
    login_pass: string,
    access_key: string, config?: AxiosRequestConfig
): Promise<StaffLoginData> => {
    try {
        let params = new URLSearchParams()
        let hash_str = sha256(login_pass)
        hash_str = sha256(hash_str.substring(32) + seed.substring(32))

        params.append('login_str', login_str)
        params.append('login_pass', hash_str)
        params.append('access_key', access_key)

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/login',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return {
            stf_id: parseInt(res.data.stf_id),
            stf_name: res.data.stf_name,
            stf_access_list: res.data.stf_access_list.split(",")
        }
    } catch (error) {
        throw error
    }
}

export const logout = async (config?: AxiosRequestConfig): Promise<void> => {
    try {
        // let params = new URLSearchParams()

        await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/logout', undefined, config
        )
    } catch (error) {
        throw error
    }
}

/**
 * getAdminStore
 * @returns 
 */
export const getAdminStore = async (config?: AxiosRequestConfig): Promise<{ list: StoreData[] }> => {
    try {
        // let params = new URLSearchParams()

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/get_admin_store', undefined, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return {
            list: res.data.list.map((item: ObjectToStoreDataProps) => ObjectToStoreData(item))
        }
    } catch (error) {
        throw error
    }
}

export const getLoginData = async (
    access_key: string, config?: AxiosRequestConfig
): Promise<StaffLoginData> => {
    try {
        let params = new URLSearchParams()
        params.append('access_key', access_key)

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/get_login_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return {
            stf_id: parseInt(res.data.stf_id),
            stf_name: res.data.stf_name,
            stf_access_list: res.data.stf_access_list.split(",")
        }
    } catch (error) {
        throw error
    }
}

export const AccessKeyList = async (config?: AxiosRequestConfig): Promise<AccessKey[]> => {
    try {
        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/access_key_list', undefined, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return res.data.list.map((data: any) => ObjectToAccessKey(data))
    } catch (error) {
        throw error
    }
}

export const AccessLevelList = async (acle_id?: number, config?: AxiosRequestConfig): Promise<AccessLevel[]> => {
    try {
        let params = new URLSearchParams()
        if (acle_id) {
            params.append("acle_id", acle_id.toString())
        }

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/access_level_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return res.data.list.map((data: any) => ObjectToAccessLevel(data))
    } catch (error) {
        throw error
    }
}

export interface SetAccessKeyProps {
    acle_id: number
    acky_list: number[]
}
export const SetAccessKey = async ({
    acle_id,
    acky_list
}: SetAccessKeyProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append('acle_id', acle_id.toString())
        params.append('acky_list', acky_list.join(","))

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/set_access_key',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface AllStaffListProps {
    page: number
    limit: number
    show_deleted: boolean
}
export interface AllStaffListResults {
    list: StaffData[]
    rows?: number
}
export const AllStaffList = async (props?: AllStaffListProps, config?: AxiosRequestConfig): Promise<AllStaffListResults> => {
    try {
        let params = new URLSearchParams()
        if (props) {
            params.append('page', (props.page - 1).toString())
            params.append('limit', props.limit.toString())
            params.append('show_deleted', props.show_deleted.toString())
        }

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/all_staff_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return {
            list: res.data.data.map((value: any) => ObjectToStaffData(value)),
            rows: typeof res.data.rows == "string" ? parseInt(res.data.rows) : res.data.rows
        }
    } catch (error) {
        throw error
    }
}

export interface getStaffDataProps {
    stf_id: number
}
export interface getStaffDataResults {
    staffData: StaffData
}
export const getStaffData = async (props: getStaffDataProps, config?: AxiosRequestConfig): Promise<getStaffDataResults> => {
    try {
        let params = new URLSearchParams()
        params.append('stf_id', props.stf_id.toString())

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/get_staff_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return {
            staffData: ObjectToStaffData(res.data.data),
        }
    } catch (error) {
        throw error
    }
}

export interface AddStaffProps {
    staffData: StaffData
}

export const AddStaff = async (props: AddStaffProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        if (!props.staffData.login_str) {
            throw new Error("スタッフログイン名が入力されてません")
        } else if (props.staffData.login_str.length < 4 || 24 < props.staffData.login_str.length) {
            throw new Error("スタッフログイン名は4文字以上24文字以内で入力してください")
        } else {
            params.append('login_str', props.staffData.login_str)
        }
        if (!props.staffData.login_pass) {
            throw new Error("ログインパスワードが入力されてません")
        } else if (props.staffData.login_pass.length < 8 || 24 < props.staffData.login_pass.length) {
            throw new Error("ログインパスワードは8文字以上24文字以内で入力してください")
        } else if (props.staffData.login_pass !== props.staffData.login_pass_confirm) {
            throw new Error("ログインパスワードが一致していません")
        } else {
            params.append('login_pass', props.staffData.login_pass)
        }
        if (!props.staffData.stf_name) {
            throw new Error("表示名が入力されてません")
        } else if (props.staffData.stf_name.length < 2 || 32 < props.staffData.stf_name.length) {
            throw new Error("表示名は2文字以上32文字以内で入力してください")
        } else {
            params.append('stf_name', props.staffData.stf_name)
        }
        if (props.staffData.stf_mail) {
            params.append('stf_mail', props.staffData.stf_mail)
        }
        if (props.staffData.stf_tel) {
            params.append('stf_tel', props.staffData.stf_tel)
        }
        if (props.staffData.stf_code) {
            if (props.staffData.stf_code.length !== 13) {
                throw new Error("バーコードが正しくありません")
            } else {
                params.append('stf_code', props.staffData.stf_code)
            }
        }
        if (props.staffData.acle_id) {
            params.append('acle_id', props.staffData.acle_id.toString())
        } else {
            throw new Error("アクセス権限が設定されてません")
        }
        if (props.staffData.admin_str_id_list) {
            params.append('admin_str_list', props.staffData.admin_str_id_list.join(","))
        }

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/add_staff',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }

}

export interface EditStaffProps {
    staffData: StaffData
}

export const EditStaff = async (props: EditStaffProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        if (!props.staffData.login_str) {
            throw new Error("スタッフログイン名が入力されてません")
        } else if (props.staffData.login_str.length < 4 || 24 < props.staffData.login_str.length) {
            throw new Error("スタッフログイン名は4文字以上24文字以内で入力してください")
        } else {
            params.append('login_str', props.staffData.login_str)
        }
        if (!props.staffData.stf_name) {
            throw new Error("表示名が入力されてません")
        } else if (props.staffData.stf_name.length < 2 || 32 < props.staffData.stf_name.length) {
            throw new Error("表示名は2文字以上32文字以内で入力してください")
        } else {
            params.append('stf_name', props.staffData.stf_name)
        }
        if (props.staffData.stf_mail) {
            params.append('stf_mail', props.staffData.stf_mail)
        }
        if (props.staffData.stf_tel) {
            params.append('stf_tel', props.staffData.stf_tel)
        }
        if (props.staffData.stf_code) {
            if (props.staffData.stf_code.length !== 13) {
                throw new Error("バーコードが正しくありません")
            } else {
                params.append('stf_code', props.staffData.stf_code)
            }
        }
        if (props.staffData.acle_id) {
            params.append('acle_id', props.staffData.acle_id.toString())
        } else {
            throw new Error("アクセス権限が設定されてません")
        }
        if (props.staffData.admin_str_id_list) {
            params.append('admin_str_list', props.staffData.admin_str_id_list.join(","))
        }
        if (props.staffData.stf_id) {
            params.append('stf_id', props.staffData.stf_id.toString())
        } else {
            throw new Error("スタッフが選択されていません")
        }


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/edit_staff',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface DeleteStaffProps {
    stf_id: number
}

export const DeleteStaff = async (props: DeleteStaffProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append('stf_id', props.stf_id.toString())


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/del_staff',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }

}

export interface changePasswordStaffProps {
    stf_id: number
    login_pass: string
    new_login_pass: string
}

export const changePasswordStaff = async (props: changePasswordStaffProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append('stf_id', props.stf_id.toString())
        params.append('login_pass', props.login_pass)
        params.append('new_login_pass', props.new_login_pass)


        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'staff/change_password_staff',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}